import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { CompanyStateLeadApiService } from '../../empresas/services/company-state-lead-api.service';
import { CompanyStateLeadRequest } from '../models/company-state-lead.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-company-state-lead-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './company-state-lead-form.component.html'
})
export class CompanyStateLeadFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  order = 1;

  // Input validation
  nameValidationError = '';
  orderValidationError = '';

  readonly NAME_MAX = 30;
  private readonly ORDER_MAX_DIGITS = 4;

  constructor(
    private api: CompanyStateLeadApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) {}

  onNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');
    filtered = filtered.substring(0, this.NAME_MAX);

    if (filtered !== value) {
      this.nameValidationError = 'Solo se permiten letras y espacios';
    } else {
      this.nameValidationError = '';
    }

    // delay mínimo para forzar override de ngModel
    setTimeout(() => {
      this.name = filtered;
    });
  }

  onOrderChange(value: number): void {
    if (String(value).length > this.ORDER_MAX_DIGITS) {
      this.order = Number(String(value).slice(0, this.ORDER_MAX_DIGITS));
      return;
    }
    this.orderValidationError = '';
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    this.api.getById(id).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.order = item.order ?? 1;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el estado';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Estado de Empresa' : 'Nuevo Estado de Empresa';
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.name.trim()) faltantes.push('Nombre');
    if (this.order === null || this.order === undefined || this.order < 1) faltantes.push('Orden');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    if (this.nameValidationError || this.orderValidationError) {
      this.error = this.nameValidationError || this.orderValidationError;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const request: CompanyStateLeadRequest = {
      name: this.name.trim(),
      order: Number(this.order)
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Estado',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/estados']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el estado';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Estado', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-empresas/estados']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el estado';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-empresas/estados']);
  }
}
